import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAnalytics } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#3B5BDB', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#6B7280'];

export default function AnalyticsPage() {
  const { data, isLoading } = useQuery({ queryKey: ['analytics'], queryFn: getAnalytics });
  const d = data?.data;

  if (isLoading) return <div className="text-gray-400 text-sm">Loading analytics...</div>;

  const presentationData = [
    { name: 'Completed', value: d?.presentations_completed || 0 },
    { name: 'Upcoming', value: d?.presentations_upcoming || 0 },
  ];

  const taskData = [
    { name: 'Completed', value: d?.tasks_completed || 0 },
    { name: 'Pending', value: d?.tasks_pending || 0 },
  ];

  const overviewData = [
    { name: 'Fellows', value: d?.total_fellows || 0 },
    { name: 'Admins', value: d?.total_admins || 0 },
    { name: 'Meetings', value: d?.meetings_total || 0 },
    { name: 'Resources', value: d?.resources_total || 0 },
    { name: 'Announcements', value: d?.announcements_total || 0 },
  ];

  const StatBox = ({ label, value, color }) => (
    <div className="card text-center">
      <div className={`text-3xl font-black ${color || 'text-white'}`}>{value}</div>
      <div className="text-gray-500 text-sm mt-1">{label}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-white">Analytics</h1>
        <p className="text-gray-500 text-sm">Platform engagement overview</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBox label="Total Fellows" value={d?.total_fellows} color="text-primary-400" />
        <StatBox label="Presentations Completed" value={d?.presentations_completed} color="text-green-400" />
        <StatBox label="Meetings Held" value={d?.meetings_total} color="text-purple-400" />
        <StatBox label="Resources Shared" value={d?.resources_total} color="text-amber-400" />
        <StatBox label="Tasks Completed" value={d?.tasks_completed} color="text-green-400" />
        <StatBox label="Tasks Pending" value={d?.tasks_pending} color="text-orange-400" />
        <StatBox label="Upcoming Meetings" value={d?.meetings_upcoming} color="text-blue-400" />
        <StatBox label="Announcements" value={d?.announcements_total} color="text-gray-300" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="font-semibold text-white mb-4">Platform Overview</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={overviewData}>
              <XAxis dataKey="name" stroke="#6B7280" tick={{ fontSize: 12 }} />
              <YAxis stroke="#6B7280" tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #374151', borderRadius: 8, color: '#f1f5f9' }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {overviewData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h2 className="font-semibold text-white mb-4">Presentations Status</h2>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={presentationData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                {presentationData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #374151', borderRadius: 8, color: '#f1f5f9' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card lg:col-span-2">
          <h2 className="font-semibold text-white mb-4">Tasks Overview</h2>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={taskData} layout="vertical">
              <XAxis type="number" stroke="#6B7280" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" stroke="#6B7280" tick={{ fontSize: 12 }} width={80} />
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #374151', borderRadius: 8, color: '#f1f5f9' }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {taskData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
